<template>
  <div :class="{ spinner: isBusy }">
    <div class="cx-category" >
      <table v-if="year && year < 2025" class="table table-borderless table-hover">
        <thead>
          <tr class="border-bottom">
            <th>
              <div>
                <b-dropdown 
                  id="dropdown-header" 
                  boundary="window"
                  size="sm" 
                  :text="year.toString()" 
                  v-model="year"
                  class="m-1"
                >
                  <b-dropdown-header id="dropdown-header-label">{{ $t('Webportal.SelectYear') }}:</b-dropdown-header>
                  <b-dropdown-item-button v-for="(option) in loyaltyBonusYears" 
                    :key="option.Year"
                    @click="onYearChanged(option.Year)"
                    aria-describedby="dropdown-header-label">
                    {{ option.Year }}
                  </b-dropdown-item-button>
                </b-dropdown>
              </div>
            </th>
            <th colspan="4" class="text-center"><strong>{{ $t('Webportal.CX.LoyaltyBonus.Qualifier.Status') }}</strong></th>
            <th colspan="4" class="text-center border-left"><strong>{{ $t('Webportal.CX.LoyaltyBonus.CorporateSpirit') }}</strong></th>
            <th colspan="4" class="text-center border-left"><strong>{{ $t('Webportal.CX.LoyaltyBonus.FocusArea') }}</strong></th>
            <th colspan="4" class="text-center border-left"><strong>{{ $t('Webportal.CX.LoyaltyBonus.Summary') }}</strong></th>
          </tr>
          <tr class="border-bottom">
            <th colspan="5" class="text-center"><strong>{{ getPercentageString(loyaltyBonusPercentages?.QualifierMargin) }}</strong></th>
            <th colspan="4" class="text-center border-left"><strong>{{ getPercentageString(loyaltyBonusPercentages?.CorporateSpiritMargin) }}</strong></th>
            <th colspan="4" class="text-center border-left"><strong>{{ getPercentageString(loyaltyBonusPercentages?.FocusAreaMargin) }}</strong></th>
            <th colspan="4" class="text-center border-left"><strong>{{ getPercentageString(loyaltyBonusPercentages?.QualifierMargin + loyaltyBonusPercentages?.FocusAreaMargin + loyaltyBonusPercentages?.CorporateSpiritMargin) }}</strong></th>
          </tr>
          <tr class="border-bottom">
            <th class="text-center">&nbsp;</th>
            <th class="text-center">{{  $t('Webportal.CX.LoyaltyBonus.Qualifier.DataReported', { percent: getPercentageString(loyaltyBonusPercentages?.QualifierTarget)}) }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.Qualifier.MobilityProgram') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.Qualifier.DigitalServiceTool') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.Qualifier.Bonus') }}</th>
            <th class="border-left text-center">{{ $t('Webportal.CX.LoyaltyBonus.CorporateSpirit.Loyalty', { percent: getPercentageString(loyaltyBonusPercentages?.CorporateSpiritTarget) }) }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.CorporateSpirit.Sold') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.CorporateSpirit.NoOfLoyaltyEvents') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.CorporateSpirit.Bonus') }}</th>
            <th class="border-left text-center">{{ $t('Webportal.CX.LoyaltyBonus.FocusArea.IncreaseNPS') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.FocusArea.IncreaseFRFT') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.FocusArea.IncreaseRedFlagManagement') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.FocusArea.Bonus') }}</th>
            <th class="border-left text-center">{{ $t('Webportal.CX.LoyaltyBonus.Summary.SumUPE') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.Summary.TotalBonus') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.Summary.Pdf') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="loyaltyBonus in loyaltyBonuses" :key="loyaltyBonus.MonthNumber">
            <td class="text-left">{{ getMonth(loyaltyBonus.MonthNumber) }}</td>
            <template v-if="!loyaltyBonus.ClientLocationId">
              <td colspan="4" class="text-center">&nbsp;</td>
              <td colspan="4" class="text-center border-left">&nbsp;</td>
              <td colspan="4" class="text-center border-left">&nbsp;</td>
              <td colspan="4" class="text-center border-left">&nbsp;</td>
            </template>
            <template v-else>            
              <td class="text-center"><span>{{ getNumberString(loyaltyBonus.DataReportingQuality) }}%</span><BooleanDisplay class="d-inline-block ml-2" :value="loyaltyBonus?.IsQualifierFulfilled" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.HasMobilityProgram" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.HasDigitalServiceTool" :colorRange="colorRange"/></td>
              <td class="text-center">{{ getNumberString(loyaltyBonus.QualifierBonus) }}&nbsp;&#8364;</td>
              <td class="border-left text-center"><BooleanDisplay :value="loyaltyBonus.IsCorporateSpiritFulfilled" :colorRange="colorRange"/></td>
              <td class="text-center">{{ getNumberString(loyaltyBonus.NoOfSoldCars) }}</td>
              <td class="text-center">{{ getNumberString(loyaltyBonus.NoOfLoyaltyEvents) }}</td>
              <td class="text-center">{{ getNumberString(loyaltyBonus.CorporateSpiritBonus) }}&nbsp;&#8364;</td>
              <td class="border-left text-center"><BooleanDisplay :value="loyaltyBonus.HasNpsIncreased" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.HasFrftIncreased" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.IsRedFlagManagementFulfilled" :colorRange="colorRange"/></td>
              <td class="text-center">{{ getNumberString(loyaltyBonus.FocusBonus) }}&nbsp;&#8364;</td>
              <td class="border-left text-center">{{ getNumberString(loyaltyBonus.SumUpe) }}&nbsp;&#8364;</td>
              <td class="text-center">{{ getNumberString(loyaltyBonus.TotalBonus) }}&nbsp;&#8364;</td>
              <td class="text-center">
                <b-button variant="primary" size="sm"
                  v-b-tooltip.hover.nofade="toolTipDelay"
                  :title="$t('Webportal.Download')"
                  :disabled="!canGetPdfReport(loyaltyBonus.ReportFileId)"
                  @click.stop="getPdfReport(loyaltyBonus.ReportFileId)"
                >
                  <font-awesome-icon icon="file-download" fixed-width />
                </b-button>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
      <table v-else-if="year" class="table table-borderless table-hover">
        <thead>
          <tr class="border-bottom">
            <th>
              <div>
                <b-dropdown 
                  id="dropdown-header" 
                  boundary="window"
                  size="sm" 
                  :text="year.toString()" 
                  v-model="year"
                  class="m-1"
                >
                  <b-dropdown-header id="dropdown-header-label">{{ $t('Webportal.SelectYear') }}:</b-dropdown-header>
                  <b-dropdown-item-button v-for="(option) in loyaltyBonusYears" 
                    :key="option.Year"
                    @click="onYearChanged(option.Year)"
                    aria-describedby="dropdown-header-label">
                    {{ option.Year }}
                  </b-dropdown-item-button>
                </b-dropdown>
              </div>
            </th>
            <th colspan="9" class="text-center border-left"><strong>{{ $t('Webportal.CX.LoyaltyBonus.FocusArea') }} {{ getPercentageString(loyaltyBonusPercentages?.FocusAreaMargin) }}</strong></th>
            <th colspan="2" class="text-center border-left"><strong>{{ $t('Webportal.CX.LoyaltyBonus.CorporateSpirit') }} {{ getPercentageString(loyaltyBonusPercentages?.CorporateSpiritMargin) }}</strong></th>
            <th colspan="3" class="text-center border-left"><strong>{{ $t('Webportal.CX.LoyaltyBonus.Summary') }}</strong></th>
          </tr>
          <tr class="border-bottom">
            <th colspan="1" class="text-center">&nbsp;</th>
            <th colspan="1" class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.EntryCriteria') }}</th>
            <th colspan="2" class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.CustomerDataQuality') }}</th>
            <th colspan="2" class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.CustomerSatisfaction') }}</th>
            <th colspan="1" class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.Training') }}</th>
            <th colspan="2" class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.Mobility') }}</th>
            <th colspan="1" class="text-center border-left">&nbsp;</th>
            <th colspan="2" class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.LoyaltyRate') }}</th>
            <th colspan="3" class="text-center border-left">&nbsp;</th>
          </tr>
          <tr class="border-bottom">
            <th class="text-center">&nbsp;</th>
            <th class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.DataReporting') }}</th>
            <th class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.ReceivedDatasets') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus2025.ContactQuota') }}</th>
            <th class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.Nps') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus2025.RedFlagManagement ') }}</th>
            <th class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.TechnicianFulfillmentRate') }}</th>
            <th class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.MobilityNeed') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus2025.OptionPreference') }}</th>
            <th class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.FocusAreaBonusClaim') }}</th>
            <th class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.LoyaltyRate') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus2025.SpiritBonusClaim') }}</th>
            <th class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.UpeSumNet') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus2025.BonusTotal') }}</th>
            <th class="text-center">{{ $t('Webportal.CX.LoyaltyBonus.Summary.Pdf') }}</th>
          </tr>
          <tr class="border-bottom">
            <th class="text-left">{{ $t('Webportal.CX.LoyaltyBonus2025.Target') }}</th>
            <th class="text-center border-left">45%</th>
            <th class="text-center border-left">97%</th>
            <th class="text-center">80%</th>
            <th class="text-center border-left">85%</th>
            <th class="text-center">100% {{ $t('Webportal.CX.LoyaltyBonus2025.OrNoRedFlag')}}</th>
            <th class="text-center border-left">100%</th>
            <th class="text-center border-left">85%</th>
            <th class="text-center">88%</th>
            <th class="text-center border-left">&nbsp;</th>
            <th class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.SeeReport')}}</th>
            <th class="text-center">&nbsp;</th>
            <th class="text-center border-left">&nbsp;</th>
            <th class="text-center">&nbsp;</th>
            <th class="text-center">&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="loyaltyBonus in loyaltyBonuses" 
            :key="loyaltyBonus.MonthNumber" 
            v-if="loyaltyBonus.MonthNumber <= 7 || year > 2025"
          >
            <td class="text-left">{{ getMonth(loyaltyBonus.MonthNumber) }}</td>
            <template v-if="!loyaltyBonus.ClientLocationId">
              <td colspan="9" class="text-center border-left">&nbsp;</td>
              <td colspan="2" class="text-center border-left">&nbsp;</td>
              <td colspan="3" class="text-center border-left">&nbsp;</td>
            </template>
            <template v-else>
              <td class="text-center border-left"><BooleanDisplay :value="loyaltyBonus.DataReporting" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.ReceivedDatasets" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.ContactQuota" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.Nps" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.RedFlagManagement" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.TechnicianFulfillmentRate" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.MobilityNeed" :colorRange="colorRange"/></td>
              <td class="text-center"><BooleanDisplay :value="loyaltyBonus.OptionPreference" :colorRange="colorRange"/></td>
              <td class="text-center">{{ getNumberString(loyaltyBonus.FocusAreaBonusClaim) }}&nbsp;&#8364;</td>
              <td class="text-center border-left"><BooleanDisplay :value="loyaltyBonus.LoyaltyRate" :colorRange="colorRange"/></td>
              <td class="text-center">{{ getNumberString(loyaltyBonus.SpiritBonusClaim) }}&nbsp;&#8364;</td>
              <td class="text-center border-left">{{ getNumberString(loyaltyBonus.UpeSumNet) }}&nbsp;&#8364;</td>
              <td class="text-center">{{ getNumberString(loyaltyBonus.BonusTotal) }}&nbsp;&#8364;</td>
              <td class="text-center">
                <b-button variant="primary" size="sm"
                  v-b-tooltip.hover.nofade="toolTipDelay"
                  :title="$t('Webportal.Download')"
                  :disabled="!canGetPdfReport(loyaltyBonus.ReportFileId)"
                  @click.stop="getPdfReport(loyaltyBonus.ReportFileId)"
                >
                  <font-awesome-icon icon="file-download" fixed-width />
                </b-button>
              </td>
            </template>
          </tr>
          <!-- oct-dec 2025 special -->
          <template v-if="year == 2025">
            <tr>
              <td class="text-left">{{ getMonth(8) }}</td>
              <td colspan="9" class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.AugustText') }}</td>
              <td colspan="2" class="text-center border-left">&nbsp;</td>
              <td colspan="3" class="text-center border-left">&nbsp;</td>
            </tr>
            <tr class="border-bottom">
              <td class="text-left">{{ getMonth(9) }}</td>
              <td colspan="9" class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.SeptemberText') }}</td>
              <td colspan="2" class="text-center border-left">&nbsp;</td>
              <td colspan="3" class="text-center border-left">&nbsp;</td>
            </tr>
            <tr class="border-bottom">
              <td></td>
              <td colspan="9" class="text-center border-left"><b>{{ $t('Webportal.CX.LoyaltyBonus2025.ReplacementVehiclePercent') }}</b></td>
              <td colspan="2" class="text-center border-left"><b>{{ $t('Webportal.CX.LoyaltyBonus2025.eVHCPercent') }}</b></td>
              <td colspan="3" class="text-center border-left">&nbsp;</td>
            </tr>
            <tr class="border-bottom">
              <td></td>
              <td colspan="3" class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.ReplacementVehicleCount') }}</td>
              <td colspan="2" class="text-center">{{ $t('Webportal.CX.LoyaltyBonus2025.MechanicCount') }}</td>
              <td colspan="2" class="text-center">{{ $t('Webportal.CX.LoyaltyBonus2025.MechanicToReplacementVehicle') }}</td>
              <td colspan="2" class="text-center">{{ $t('Webportal.CX.LoyaltyBonus2025.ReplacementVehicleFulfilled') }}</td>
              <td colspan="1" class="text-center border-left">{{ $t('Webportal.CX.LoyaltyBonus2025.Quota') }}</td>
              <td colspan="1" class="text-center">{{ $t('Webportal.CX.LoyaltyBonus2025.EVHCFulfilled') }}</td>
              <td colspan="3" class="text-center border-left">&nbsp;</td>
            </tr>
            <tr 
              v-for="loyaltyBonus in loyaltyBonuses" 
              :key="loyaltyBonus.MonthNumber" 
              v-if="loyaltyBonus.MonthNumber > 9"
            >
              <td class="text-left">{{ getMonth(loyaltyBonus.MonthNumber) }}</td>
              <td colspan="3" class="text-center border-left">{{ loyaltyBonus.ReplacementVehicle }}</td>
              <td colspan="2" class="text-center">{{ loyaltyBonus.Mechanic }}</td>
              <td colspan="2" class="text-center">{{ loyaltyBonus.Mechanic_ReplacementVehicle }}</td>
              <td colspan="2" class="text-center"><BooleanDisplay :value="loyaltyBonus.ReplacementVehicleFulfilled" :colorRange="colorRange"/></td>
              <td colspan="1" class="text-center border-left" v-if="loyaltyBonus.EVHC || loyaltyBonus.EVHC == 0">{{ loyaltyBonus.EVHC }}%</td>
              <td colspan="1" class="text-center border-left" v-else></td>
              <td colspan="1" class="text-center"><BooleanDisplay :value="loyaltyBonus.EVHCFulfilled" :colorRange="colorRange"/></td>
              <td colspan="1" class="text-center border-left">{{ getNumberString(loyaltyBonus.UpeSumNetQ4) }}&nbsp;&#8364;</td>
              <td colspan="1" class="text-center">{{ getNumberString(loyaltyBonus.BonusTotalQ4) }}&nbsp;&#8364;</td>
              <td colspan="1" class="text-center">
                <b-button variant="primary" size="sm"
                  v-b-tooltip.hover.nofade="toolTipDelay"
                  :title="$t('Webportal.Download')"
                  :disabled="!canGetPdfReport(loyaltyBonus.ReportFileId)"
                  @click.stop="getPdfReport(loyaltyBonus.ReportFileId)"
                >
                  <font-awesome-icon icon="file-download" fixed-width />
                </b-button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
import AppConfig from '@/mixins/AppConfig'
import BrandedCard from '@/components/CX/DashboardCards/BrandedCard';
import DownloadFileMixin from '@/mixins/DownloadFileMixin'
import DashboardService from "@/services/DashboardService-APP_TARGET";
import BooleanDisplay from './BooleanDisplay.vue';
import ReportFileService from "@/services/ReportFileService"

import moment from "moment";

export default {
  components: {
    BrandedCard,
    BooleanDisplay
  },
  mixins: [ AppConfig , DownloadFileMixin],
  data(){
    return {
      loyaltyBonuses: [],
      loyaltyBonusPercentages: null,
      isBusy: false,
      year: null
    }
  },
  mounted(){      
    if(this.loyaltyBonusYears) {
      this.year = this.loyaltyBonusYears[0].Year;
      for (let i = 1; i < this.loyaltyBonusYears.length; i++) {
        if (this.loyaltyBonusYears[i].Year > this.year) {
          this.year = this.loyaltyBonusYears[i].Year;
        }
      }
    }
    else this.year = new Date().getUTCFullYear();
    this.init();
  },
  
  props: {
    clientLocationId: { type: Number, required: true },
    colorRange:       { type: Array,  required: true },
    loyaltyBonusYears: {type: Array },
  },
  methods: {
    async init(){
      this.isBusy = true;
      this.loyaltyBonuses = [];
      this.loyaltyBonusPercentages = null;
      this.loyaltyBonuses = await this.loadLoyaltyBonusData();
      this.loyaltyBonusPercentages = await this.loadLoyaltyBonusPercentages();
      this.isBusy = false;
    },
    async loadLoyaltyBonusData() {
      return DashboardService.GetLoyaltyBonusData(this.clientLocationId, this.year)
      .then(res => {
        return res.data;
      })
      .catch(error => {
        console.error("GetLoyaltyBonusData failed", error)
      })
    },
    async loadLoyaltyBonusPercentages() {
      return DashboardService.GetLoyaltyBonusPercentages(this.year)
      .then(res => {
        return res.data;
      })
      .catch(error => {
        console.error("GetLoyaltyBonusPercentages failed", error)
      })
    },
    canGetPdfReport(reportFileId){
      return reportFileId != null;
    },
    async getPdfReport(reportFileId){
      this.isBusy = true;
      
      try {
        const response = await ReportFileService.downloadReportFile(reportFileId);
        this.downloadFile(response);
      } finally {
        this.isBusy = false;
      }
    },
    onYearChanged(value){
      if(this.year == value)
      {
        return;
      }
      this.year = value;
      this.init();
    },
    getMonth(month){
      return moment(new Date(2000, month-1, 1)).format('MMM');
    },
    getNumberString(n){
      if(!n)
        return '-';
      return n.toLocaleString();
    },
    getPercentageString(p) {
      if(!p) {
        return '-';  
      }
      
      return `${(p * 100).toLocaleString()}%`
    },
  }
}
</script>
<style scoped>
.table thead th {
  vertical-align: middle;
}
</style>